<template>
  <div id="customers-list-div" class="push-me-downwards border pt-1 px-1">

    <b-container class="px-0">
      <b-row>
        <b-col cols="7" class="right">
          <b-button
              v-if="canCreateCustomers"
              variant="primary"
              size="sm"
              class="mb-1"
              @click="openCustomerModal()"
          >
            <span>Create new customer</span>
          </b-button>
        </b-col>
      </b-row>
    </b-container>

    <b-container v-if="chosenCustomer != null">
      <b-row class="selected-customers-row align-items-center mx-0">
        <b-col
          cols="2"
          md="1"
          class="p-0 text-center"
        >
          <img
            :src="'/static_images/unisex-avatar.png'"
            :style="{ maxWidth: '50px', border: '1px solid #DDDDDD' }"
            class="mr-1"
          >
        </b-col>
        <b-col
          cols="10"
          md="3"
          class="p-0 text-left text-md-left"
        >
          <span class="mt-2">{{ chosenCustomer.name }}</span>
          <span class="d-md-none">
            <br>
            <span class="mt-2">{{ chosenCustomer.phone }}</span>
            <br>
            <span class="mt-2">{{ chosenCustomer.email }}</span>
            <br>
            <span class="mt-2">{{ chosenCustomerCardLabel }}</span>
          </span>
        </b-col>
        <b-col
          md="6"
          class="p-0 text-center d-none d-md-block"
        >
          <span class="mt-2">{{ chosenCustomer.phone }}</span>
          <br>
          <span class="mt-2">{{ chosenCustomer.email }}</span>
        </b-col>
        <b-col
          md="2"
          class="p-0 text-center d-none d-md-block"
        >
          <span class="mt-2">{{ chosenCustomerCardLabel }}</span>
        </b-col>
      </b-row>
    </b-container>

    <b-container class="px-0">
      <b-row class="py-2">
        <b-col aria-colspan="6">
          <b-form-input
              v-model="search_keyword"
              placeholder="Search existing customer"
          />
        </b-col>
      </b-row>

      <div class="scrollable-container" v-show="customers.value != undefinded && customers.value.length > 0">
        <b-row
            v-for="(customer, index) in customers.value"
            :key="customer.id"
            class="customer-search-row pb-1 my-1 mx-0"
            :class="{ 'customer-search-row--border': index < customers.value.length - 1 }"
        >
          <b-col
            cols="8"
            md="4"
            class="p-0 text-left small-horizontal-padding"
          >
            <span class="mt-2">{{ customer.name }}</span>
            <span class="d-md-none">
              <br>
              <span class="mt-2">{{ customer.phone }}</span>
              <br>
              <span class="mt-2">{{ customer.email }}</span>
            </span>
          </b-col>
          <b-col
            md="6"
            class="p-0 text-left d-none d-md-block"
          >
            <span class="mt-2">{{ customer.phone }}</span>
            <br>
            <span class="mt-2">{{ customer.email }}</span>
          </b-col>
          <b-col
            cols="4"
            md="2"
            class="p-0 text-right small-horizontal-padding"
          >
            <span
              class="btn btn-dark btn-sm"
              @click="updateCustomer(customer.id)"
            >Select</span>
          </b-col>
        </b-row>
      </div>
    </b-container>

  </div>
  <ManageCustomer
      :click-date="clickDate"
      :customer="null"
  />
</template>
<script setup>
import { watch, ref, reactive, provide, computed } from 'vue';
import { useStore } from "vuex";
import {
  BButton, BCol,
  BFormGroup,
  BFormInput,
  BRow,
} from "bootstrap-vue";
import { toastFailure } from '@/libs/toastification';
import ManageCustomer from "@/views/Components/ManageCustomer.vue";
import { ORDER_CUSTOMER_SELECT_KEY } from '@/constants/injectionKeys';
import { formatCardLast4 } from '@/libs/utils';
import { canStaffCreate } from '@/libs/staffPermissions';

const props = defineProps({
  customer: {
    type: Object,
    default: null,
  }
});

const search_keyword = ref('');
const customers = reactive({ value: [] });
const store = useStore();
const emit = defineEmits(['update:customer']);
const clickDate = ref(null);
const chosenCustomer = ref(null);

const chosenCustomerCardLabel = computed(() => formatCardLast4(chosenCustomer.value?.card_last_4));
const canCreateCustomers = computed(() => canStaffCreate(store.getters['auth/user'], 'customers'));

const removeFromSearchResults = (customerId) => {
  customers.value = customers.value.filter((customer) => customer.id !== customerId);
};

const selectCreatedCustomer = (customer) => {
  if (!customer?.id) {
    return;
  }

  chosenCustomer.value = customer;
  removeFromSearchResults(customer.id);
  emit('update:customer', customer);
};

provide(ORDER_CUSTOMER_SELECT_KEY, selectCreatedCustomer);

watch(
  () => store.state.customers.orderCreatedCustomer,
  (customer) => {
    if (!customer?.id) {
      return;
    }

    selectCreatedCustomer(customer);
    store.commit('customers/SET_ORDER_CREATED_CUSTOMER', null);
  },
);

watch(
  () => props.customer,
  (customer) => {
    if (customer?.id) {
      chosenCustomer.value = customer;
    }
  },
  { immediate: true },
);

const openCustomerModal = () => {
  clickDate.value = new Date();
};

const updateCustomer = async (customerId) => {
  store.dispatch('customers/loadCustomer', customerId)
      .then((response) => {
        const payload = response?.data ?? response;

        selectCreatedCustomer({
          id: customerId,
          name: payload.name,
          email: payload.email,
          phone: payload.phone,
          card_last_4: payload.card_last_4,
          description: payload.description,
          addresses: payload.addresses,
        });
      })
      .catch(() => {
        toastFailure('Could not load the selected customer');
      });
};

watch(search_keyword, (new_keyword) => {
  if (!new_keyword) {
    customers.value = [];
    return;
  }

  store.dispatch('customers/searchCustomer', new_keyword)
      .then((response) => {
        customers.value = response.data;
      })
      .catch(() => {

      });
});

</script>

<style scoped>
.customer-search-row {
  width: 100%;
  flex-wrap: nowrap;
}

.customer-search-row--border {
  border-bottom: 1px solid #dae1e7;
}

@media (max-width: 767.98px) {
  #customers-list-div {
    font-size: 0.75rem;
  }

  #customers-list-div :deep(.form-control),
  #customers-list-div :deep(.btn),
  #customers-list-div :deep(small) {
    font-size: 0.7rem;
  }

  #customers-list-div :deep(img) {
    max-width: 36px !important;
  }
}
</style>
